// B1.4: move-only from client.ts.
import { ApiClientCore, ApiError, buildQueryString } from './http/core';

export class WalletApi extends ApiClientCore {
  async archiveWalletTemplate(templateId: number): Promise<any> {
    return this.post(`/api/v1/wallet/templates/${templateId}/archive`);
  }
  async createWalletAdjustment(
    envelopeId: number,
    data: { amount: number; comment?: string; reconciliation_id?: number }
  ): Promise<any> {
    return this.post(`/api/v1/wallet/envelopes/${envelopeId}/adjustments`, data);
  }
  async createWalletDefaultTemplate(): Promise<{ template_id: number; created: boolean }> {
    return this.post('/api/v1/wallet/templates/default-preset');
  }
  async createWalletEnvelope(data: {
    location_id: number;
    template_id: number;
    name: string;
  }): Promise<any> {
    return this.post('/api/v1/wallet/envelopes', data);
  }
  async createWalletReconciliation(
    envelopeId: number,
    data: { actual_amount: number; comment?: string; photo_file_id?: number },
  ): Promise<{
    id: number;
    system_balance: number;
    actual_amount: number;
    delta: number;
    has_mismatch: boolean;
  }> {
    return this.post(`/api/v1/wallet/envelopes/${envelopeId}/reconciliations`, data);
  }
  async deleteArchivedWalletEnvelope(envelopeId: number): Promise<{
    envelope_id: number;
    deleted: boolean;
    operation_count: number;
    adjustment_count: number;
  }> {
    return this.delete(`/api/v1/wallet/envelopes/${envelopeId}`);
  }
  async deleteArchivedWalletTemplate(templateId: number): Promise<{
    template_id: number;
    deleted: boolean;
    operation_count: number;
    archived_envelope_count: number;
  }> {
    return this.delete(`/api/v1/wallet/templates/${templateId}`);
  }
  async downloadWalletEnvelopeQrPrintPdf(envelopeIds: number[]): Promise<void> {
    if (envelopeIds.length !== 1) {
      throw new Error('Печать одного конверта за раз');
    }
    const { parseContentDispositionFilename } = await import('@/lib/parse-content-disposition');
    const qs = encodeURIComponent(String(envelopeIds[0]));
    const url = `${this.baseURL}/api/v1/wallet/envelopes/qr/print.pdf?envelope_ids=${qs}`;
    const response = await this.authedFetch(url, { method: 'GET' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        (errorData as any).detail || (errorData as any).error || response.statusText,
        errorData,
      );
    }
    const filename =
      parseContentDispositionFilename(response.headers.get('Content-Disposition')) ||
      'QR наклейка.pdf';
    const blob = await response.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  async getWalletEnvelopeLedger(
    envelopeId: number,
    params: Record<string, unknown> = {},
  ): Promise<{ balance?: Record<string, unknown>; entries: any[] }> {
    return this.get(`/api/v1/wallet/envelopes/${envelopeId}/ledger${buildQueryString(params)}`);
  }
  async getWalletEnvelopeLedgerAll(
    envelopeId: number,
    params: Record<string, unknown> = {},
  ): Promise<{ balance?: Record<string, unknown>; entries: any[] }> {
    const pageSize = 200;
    let balance: Record<string, unknown> | undefined;
    const allEntries: any[] = [];
    let offset = 0;
    for (;;) {
      const res = await this.getWalletEnvelopeLedger(envelopeId, {
        ...params,
        limit: pageSize,
        offset,
      });
      if (res.balance) balance = res.balance;
      const entries = Array.isArray(res.entries) ? res.entries : [];
      allEntries.push(...entries);
      if (entries.length < pageSize) break;
      offset += pageSize;
      if (offset > 10_000) break;
    }
    return { balance, entries: allEntries };
  }
  async getWalletEnvelopeQr(id: number): Promise<any> {
    return this.get(`/api/v1/wallet/envelopes/${id}/qr`);
  }
  async getWalletEnvelopeUsage(envelopeId: number): Promise<{
    envelope_id: number;
    envelope_name: string;
    is_active: boolean;
    operation_count: number;
    adjustment_count: number;
  }> {
    return this.get(`/api/v1/wallet/envelopes/${envelopeId}/usage`);
  }
  async getWalletEnvelopes(params: Record<string, unknown> = {}): Promise<any[]> {
    return this.get(`/api/v1/wallet/envelopes${buildQueryString(params)}`);
  }
  async getWalletHistory(params: Record<string, unknown> = {}): Promise<any[]> {
    return this.get(`/api/v1/wallet/history${buildQueryString(params)}`);
  }
  async getWalletHistoryAll(params: Record<string, unknown> = {}): Promise<any[]> {
    const pageSize = 200;
    const all: any[] = [];
    let offset = 0;
    for (;;) {
      const batch = await this.getWalletHistory({ ...params, limit: pageSize, offset });
      const rows = Array.isArray(batch) ? batch : [];
      all.push(...rows);
      if (rows.length < pageSize) break;
      offset += pageSize;
      if (offset > 10_000) break;
    }
    return all;
  }
  async getWalletSummary(params: Record<string, unknown> = {}): Promise<any> {
    return this.get(`/api/v1/wallet/summary${buildQueryString(params)}`);
  }
  async getWalletTemplateCalcRoles(templateId: number): Promise<any> {
    return this.get(`/api/v1/wallet/templates/${templateId}/calc-roles`);
  }
  async getWalletTemplateUsage(templateId: number): Promise<{
    template_id: number;
    envelope_count: number;
    envelope_names: string[];
    operation_count: number;
    last_used_at?: string | null;
    can_delete: boolean;
  }> {
    return this.get(`/api/v1/wallet/templates/${templateId}/usage`);
  }
  async openWalletManual(envelopeId: number): Promise<{
    checklist_id: number;
    envelope_id: number;
    envelope_name?: string;
    template_id: number;
    template_name?: string;
    location_id: number;
    location_name?: string;
    deep_link_path?: string;
    created: boolean;
  }> {
    return this.post(`/api/v1/wallet/envelopes/${envelopeId}/open-manual`, {});
  }
  async putWalletTemplateCalcRoles(
    templateId: number,
    items: Array<{
      template_item_id: number;
      calc_role: string;
      sign?: number;
      custom_label?: string;
    }>
  ): Promise<any> {
    return this.put(`/api/v1/wallet/templates/${templateId}/calc-roles`, { items });
  }
  async restoreWalletTemplate(templateId: number): Promise<any> {
    return this.post(`/api/v1/wallet/templates/${templateId}/restore`);
  }
  async setWalletEnvelopeInitialBalance(id: number, amount: number): Promise<any> {
    return this.post(`/api/v1/wallet/envelopes/${id}/initial-balance`, { amount });
  }
  async updateWalletEnvelope(
    id: number,
    data: {
      name?: string;
      template_id?: number;
      is_active?: boolean;
      recount_enabled?: boolean;
      recount_interval_hours?: number | null;
    }
  ): Promise<any> {
    return this.patch(`/api/v1/wallet/envelopes/${id}`, data);
  }
  async getWalletFormEmployees(
    locationId: number,
    atLocationOnly = false,
  ): Promise<{ items: Array<{ id: number; full_name: string }> }> {
    return this.get(
      `/api/v1/wallet/form-employees${buildQueryString({
        location_id: locationId,
        at_location_only: atLocationOnly,
      })}`,
    );
  }
}
